import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import type { CSSProperties } from 'styled-components';
import styled, { css } from 'styled-components';

import type { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { Glitch } from '@sorare/core/src/atoms/animations/Glitch';
import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { Tooltip } from '@sorare/core/src/atoms/tooltip/Tooltip';
import { BrandXS } from '@sorare/core/src/atoms/typography';
import { useTouchScreen } from '@sorare/core/src/hooks/device/useTouchScreen';
import { getRarityColor } from '@sorare/core/src/lib/colors';
import {
  getPositionInitials,
  getPositionName,
} from '@sorare/core/src/lib/positions';
import type { Font } from '@sorare/core/src/lib/text';
import { getTextSize } from '@sorare/core/src/lib/text';
import { borderGradientStyle } from '@sorare/core/src/style/utils';

import competitionClue from '#src/assets/card-pull-experience/competition.svg';
import divisionClue from '#src/assets/card-pull-experience/division.svg';
import globalCupBadge from '#src/assets/card-pull-experience/global-cup-badge.svg';
import countryClue from '#src/assets/card-pull-experience/nationality.svg';
import positionClue from '#src/assets/card-pull-experience/position.svg';
import { CardPlaceholder } from '#src/components/essence/CardPullExperience/CardPlaceholder/index.tsx';

import type { RevealingCardPlaceholder_cardPulledCard } from './__generated__/index.graphql.ts';

const StyledCardPlaceholder = styled(CardPlaceholder)`
  height: 100%;
  width: 100%;
  border-radius: 4.16%;
`;

const GlobalCupBorder = styled.div`
  ${borderGradientStyle}
  position: absolute;
  inset: 0;
  border-radius: 4.16%;
  --border-gradient: var(--c-gradient-score-max);
  --border-size: 2px;
  pointer-events: none;
`;

const GlobalCupBadge = styled.img`
  position: absolute;
  top: var(--unit);
  right: var(--unit);
  width: 18%;
  height: auto;
  pointer-events: none;
`;
const Wrapper = styled(ButtonBase)<{ selected: boolean }>`
  position: relative;
  display: block;
  transition: transform 0.2s ease-out;

  &:before {
    content: '';
    border-radius: var(--unit);
    position: absolute;
    inset: 0;
    box-shadow: 0px 0px 40px 0 var(--color);
    opacity: 0;
    transition: opacity 0.2s ease-out;
  }
  &:focus-visible {
    outline: none;
  }
  &:hover:not(:disabled),
  &:focus:not(:disabled) {
    transform: scale(1.05);
    &:before {
      opacity: 1;
    }
  }
  ${({ selected }) =>
    selected &&
    css`
      &:before {
        opacity: 1;
      }
    `}
`;

const AttributesWrapper = styled(Horizontal).attrs({
  gap: 1,
})`
  position: absolute;
  bottom: 15%;
  left: var(--half-unit);
  right: var(--half-unit);
`;

const CraftingClue = styled.img`
  height: var(--double-unit);
  object-fit: contain;
  mask-image: linear-gradient(var(--c-nd-600), var(--c-nd-600));
`;

const StyledGlitch = styled(Glitch)`
  height: 32px;
  max-width: 56px;
  img {
    height: 22px;
    max-width: 56px;
    object-fit: contain;
  }
  &.country {
    border-radius: var(--half-unit);
    overflow: hidden;
  }
`;

const DIVISION_FONT_SIZE = 14;
const DIVISION_MAX_WIDTH = 80;
const DIVISION_MAX_HEIGHT = 40;
const getDivisionFont = (size: number): Font => `normal ${size}px pressio`;

const getDivisionNameStyle = (
  name: string
): { fontSize?: number; lineHeight?: string } => {
  const words = name.split(' ');
  if (words.length <= 1) return {};

  // Each word wraps to its own line within 80px
  const fontSize = Math.min(
    DIVISION_FONT_SIZE,
    Math.floor(DIVISION_MAX_HEIGHT / words.length)
  );
  if (fontSize === DIVISION_FONT_SIZE) return {};

  // Verify the longest word fits at the reduced font size
  const longestWord = words.reduce((a, b) => (a.length > b.length ? a : b));
  const { width } = getTextSize(
    longestWord.toUpperCase(),
    getDivisionFont(fontSize)
  );
  const adjustedSize =
    width > DIVISION_MAX_WIDTH
      ? fontSize * (DIVISION_MAX_WIDTH / width)
      : fontSize;
  return { fontSize: adjustedSize, lineHeight: `${adjustedSize}px` };
};

type Props = {
  rarity: Rarity;
  selected: boolean | null;
  onClick?: () => void;
  disabled: boolean;
  cardPulledCard: RevealingCardPlaceholder_cardPulledCard;
};

export const RevealingCardPlaceholder = ({
  rarity,
  selected,
  onClick,
  disabled,
  cardPulledCard,
}: Props) => {
  const isTouchScreen = useTouchScreen();
  const { country, competition, position, division, atGlobalCup } =
    cardPulledCard;
  const divisionStyle = useMemo(
    () => (division ? getDivisionNameStyle(division.name) : {}),
    [division]
  );
  const attributeRevealed = [
    position,
    division?.name,
    competition?.slug,
    country?.slug,
    atGlobalCup,
  ].filter(value => value !== null && value !== undefined);

  return (
    <Wrapper
      data-revealing-card
      onClick={onClick}
      disabled={disabled}
      selected={selected ?? false}
      style={
        {
          '--color': getRarityColor(rarity),
        } as CSSProperties
      }
    >
      <StyledCardPlaceholder rarity={selected === false ? undefined : rarity}>
        {attributeRevealed.length === 0 && (
          <path
            opacity="0.4"
            d="M62.2844 131.742V117.158L75.5727 111.183V96.2229H59.4011V108.675H48.9961V101.99C48.9961 91.9606 54.1359 87.364 67.466 87.364C80.7961 87.364 85.9777 91.9606 85.9777 101.99V115.696L72.6894 121.671V131.742H62.2844ZM61.4069 149V137.926H73.5669V149H61.4069Z"
            fill="white"
            fillOpacity="0.43"
          />
        )}
        {!!selected && (
          <text
            fill="white"
            fontSize="11px"
            fontWeight="bold"
            x="50%"
            y="95%"
            textLength="100px"
            textAnchor="middle"
            lengthAdjust="spacing"
            style={{ whiteSpace: 'nowrap' }}
          >
            {isTouchScreen ? (
              <FormattedMessage defaultMessage="Tap to confirm" />
            ) : (
              <FormattedMessage defaultMessage="Click to confirm" />
            )}
          </text>
        )}
      </StyledCardPlaceholder>
      <AttributesWrapper center>
        {!!country && (
          <Tooltip placement="top" title={country.name}>
            <Vertical center>
              <CraftingClue src={countryClue} alt="" />

              <StyledGlitch className="country">
                <img src={country.flagUrl} alt={country.name} />
              </StyledGlitch>
            </Vertical>
          </Tooltip>
        )}
        {!!competition?.pictureUrl && (
          <Tooltip placement="top" title={competition.displayName}>
            <Vertical>
              <CraftingClue src={competitionClue} alt="" />
              <StyledGlitch>
                <img
                  src={competition.pictureUrl}
                  alt={competition.displayName}
                />
              </StyledGlitch>
            </Vertical>
          </Tooltip>
        )}
        {!!position && (
          <Tooltip placement="top" title={getPositionName(position)}>
            <Vertical center>
              <CraftingClue src={positionClue} alt="" />
              <StyledGlitch>
                <BrandXS>{getPositionInitials(position)}</BrandXS>
              </StyledGlitch>
            </Vertical>
          </Tooltip>
        )}
        {!!division && (
          <Tooltip placement="top" title={division.name}>
            <Vertical center>
              <CraftingClue src={divisionClue} alt="" />
              <StyledGlitch>
                <BrandXS style={divisionStyle}>{division.name}</BrandXS>
              </StyledGlitch>
            </Vertical>
          </Tooltip>
        )}
      </AttributesWrapper>
      {atGlobalCup === true && selected !== false && (
        <>
          <GlobalCupBorder />
          <GlobalCupBadge src={globalCupBadge} alt="" />
        </>
      )}
    </Wrapper>
  );
};

RevealingCardPlaceholder.fragments = {
  cardPulledCard: gql`
    fragment RevealingCardPlaceholder_cardPulledCard on CardPullCard {
      id
      country {
        slug
        name
        flagUrl
      }
      competition {
        slug
        displayName
        pictureUrl
      }
      position
      division {
        name
      }
      atGlobalCup
    }
  ` as TypedDocumentNode<RevealingCardPlaceholder_cardPulledCard>,
};
